"use client";

import { useState, useEffect } from "react";

export default function AnalyticsChart({ data }) {
  const [activeMetric, setActiveMetric] = useState("orders");
  const [chartData, setChartData] = useState([0, 0, 0, 0, 0, 0, 0]);
  const daysOrder = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  useEffect(() => {
    updateChartData(data);
  }, [data, activeMetric]);

  const updateChartData = (apiData) => {
    if (!apiData) {
      setChartData([0, 0, 0, 0, 0, 0, 0]);
      return;
    }

    if (activeMetric === "orders") {
      const ordersData = daysOrder.map((day) => {
        const fullDay = getFullDayName(day);
        return apiData.orders[fullDay] || 0;
      });
      setChartData(ordersData);
    } else {
      const earningsData = daysOrder.map((day) => {
        const fullDay = getFullDayName(day);
        const dayData = apiData.earnings[fullDay];
        return dayData ? (typeof dayData.sum === "number" ? dayData.sum : parseFloat(dayData.sum)) || 0 : 0;
      });
      setChartData(earningsData);
    }
  };

  const getFullDayName = (shortDay) => {
    const dayMap = {
      Mo: "Mon",
      Tu: "Tue",
      We: "Wed",
      Th: "Thu",
      Fr: "Fri",
      Sa: "Sat",
      Su: "Sun",
    };
    return dayMap[shortDay] || shortDay;
  };

  const maxValue = Math.max(...chartData, 1);
  const normalizedData = chartData.map((value) => (value / maxValue) * 100);

  const createSmoothPath = (data) => {
    const width = 400;
    const height = 160;
    const padding = 40;
    const xStep = (width - padding * 2) / (data.length - 1);
    const yScale = height / 100;

    const points = data.map((value, index) => ({
      x: padding + index * xStep,
      y: height - value * yScale,
    }));

    if (points.length < 2) return "";

    let path = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length; i++) {
      const current = points[i];
      const previous = points[i - 1];
      const next = points[i + 1] || current;
      const prev2 = points[i - 2] || previous;

      const tension = 0.4;
      const cp1x = previous.x + ((current.x - prev2.x) * tension) / 6;
      const cp1y = previous.y + ((current.y - prev2.y) * tension) / 6;
      const cp2x = current.x - ((next.x - previous.x) * tension) / 6;
      const cp2y = current.y - ((next.y - previous.y) * tension) / 6;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${current.x} ${current.y}`;
    }

    return path;
  };

  const createAreaPath = (data) => {
    const linePath = createSmoothPath(data);
    const width = 400;
    const height = 160;
    const padding = 40;
    return `${linePath} L ${width - padding} ${height} L ${padding} ${height} Z`;
  };

  const getPointPosition = (index, value) => {
    const width = 400;
    const height = 160;
    const padding = 40;
    const xStep = (width - padding * 2) / (chartData.length - 1);
    const yScale = height / 100;

    return {
      x: padding + index * xStep,
      y: height - value * yScale,
    };
  };

  const maxIndex = chartData.indexOf(Math.max(...chartData));
  const maxPoint = getPointPosition(maxIndex, normalizedData[maxIndex]);

  return (
    <div style={{ width: "100%", maxWidth: "600px", margin: "0 auto", padding: "24px" }}>
      <h2 style={{ fontSize: "20px", fontWeight: "500", color: "#1f2937", margin: "0 0 24px 0" }}>
        Analytics
      </h2>

      <div style={{ background: "#CED5FF80", borderRadius: "30px", padding: "20px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}>
        <div style={{ display: "flex", gap: "8px", marginBottom: "25px", justifyContent: "center" }}>
          <button
            onClick={() => setActiveMetric("orders")}
            style={{
              padding: "2px 20px",
              borderRadius: "5px",
              fontWeight: "600",
              fontSize: "12px",
              backgroundColor: activeMetric === "orders" ? "#3853EA" : "rgba(255, 255, 255, 0.7)",
              color: activeMetric === "orders" ? "#ffffff" : "#6b7280",
              border: "none",
              cursor: "pointer",
            }}
          >
            Orders
          </button>
          <button
            onClick={() => setActiveMetric("earnings")}
            style={{
              padding: "2px 20px",
              borderRadius: "5px",
              fontWeight: "600",
              fontSize: "12px",
              backgroundColor: activeMetric === "earnings" ? "#3853EA" : "rgba(255, 255, 255, 0.7)",
              color: activeMetric === "earnings" ? "#ffffff" : "#6b7280",
              border: "none",
              cursor: "pointer",
            }}
          >
            Earnings
          </button>
        </div>

        <svg width="100%" height="220" viewBox="0 0 400 220" style={{ overflow: "visible" }}>
          {/* Grid lines and labels */}
          {daysOrder.map((day, i) => {
            const x = 40 + i * ((400 - 80) / 6);
            return (
              <g key={day}>
                <line x1={x} y1={40} x2={x} y2={160} stroke="#3853EA" strokeWidth="1" opacity="0.5" />
                <text x={x} y={190} textAnchor="middle" fontSize="14" fill="#3853EA" opacity="0.8">
                  {day}
                </text>
              </g>
            );
          })}

          {/* Area fill */}
          <path
            d={createAreaPath(normalizedData)}
            fill="url(#areaGradient)"
            style={{ transition: "all 0.5s ease-in-out" }}
          />

          {/* Line */}
          <path
            d={createSmoothPath(normalizedData)}
            fill="none"
            stroke="#3853EA"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transition: "all 0.5s ease-in-out" }}
          />

          {/* Indicator dot */}
          {maxValue > 1 && (
            <circle
              cx={maxPoint.x}
              cy={maxPoint.y}
              r="6"
              fill="#fbbf24"
              stroke="white"
              strokeWidth="3"
              style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))" }}
            />
          )}

          <defs>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3853EA" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3853EA" stopOpacity="0.1" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}